import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Button,
  CircleRating,
  FilterIcon,
  TableColumns,
  TableColumnsColumnProps,
  Tag,
  Text,
} from '@mercai/clever'
import { startOfMonth, endOfMonth, format } from 'date-fns'
import { AppliedFilters, MonthlyFilter } from '@/components'
import { formatNumber, useParamsState } from '@/helpers'

import { useAIDashboardMonthlyMetrics } from '@/services/ai-conversation.service'

import { DashboardDrawerFilter } from './components'

import styles from './dashboard.module.css'

export const Dashboard = () => {
  const { t } = useTranslation('screenAiCenter')
  const { t: tEnums } = useTranslation('enums')

  const [filter, setFilter] = useParamsState({
    dateAtAfter: format(startOfMonth(new Date()), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
    dateAtBefore: format(endOfMonth(new Date()), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
    role: '',
  })

  const [isOpenDrawerFilter, setIsOpenDrawerFilter] = useState(false)

  const getMonthFilter = () => {
    const date = filter.dateAtBefore ? new Date(filter.dateAtBefore) : undefined

    return date
  }

  const onSelectMonthFilter = (date: Date) => {
    const selectedDate = new Date(date)
    const startOfMonthDate = startOfMonth(selectedDate)
    const endOfMonthDate = endOfMonth(selectedDate)

    const startOfMonthISO = format(startOfMonthDate, "yyyy-MM-dd'T'HH:mm:ss'Z'")
    const endOfMonthISO = format(endOfMonthDate, "yyyy-MM-dd'T'HH:mm:ss'Z'")

    setFilter({
      ...filter,
      dateAtAfter: startOfMonthISO,
      dateAtBefore: endOfMonthISO,
    })
  }

  const { data: monthlyMetrics, isLoading: isLoadingMonthlyMetrics } =
    useAIDashboardMonthlyMetrics({
      dateAtAfter: filter.dateAtAfter,
      dateAtBefore: filter.dateAtBefore,
      role: filter.role,
    })

  const columns: TableColumnsColumnProps<{
    month: string
    totalConversations: number
    jtbdCompleted: number
    jtbdCompletionPercentage: number
    avgGeneralNote: number
  }>[] = [
    {
      key: 'month',
      title: t('dashboardMonth'),
      dataKey: 'month',
    },
    {
      title: t('dashboardAvgGeneralNote'),
      key: 'avgGeneralNote',
      width: '8rem',
      dataKey: 'avgGeneralNote',
      render: ({ value }) => {
        return <CircleRating rating={+(value || 0)} suffix="" />
      },
    },
    {
      key: 'totalConversations',
      title: t('dashboardTotalConversations'),
      dataKey: 'totalConversations',
      render: ({ value }) => {
        return (
          <Text>
            {value} {t('dashboardConversations')}
          </Text>
        )
      },
    },
    {
      key: 'jtbdCompleted',
      title: t('dashboardJtbdCompleted'),
      dataKey: 'jtbdCompleted',
      render: ({ value }) => {
        return (
          <Text>
            {value} {t('dashboardConversations')}
          </Text>
        )
      },
    },
    {
      key: 'jtbdCompletionPercentage',
      title: t('dashboardJtbdCompletionPercentage'),
      dataKey: 'jtbdCompletionPercentage',
      render: ({ value }) => {
        return <Text>{formatNumber(+(value || 0) * 100)}%</Text>
      },
    },
  ]

  const agentColumns: TableColumnsColumnProps<{
    agent: string
    totalEvaluations: number
    jtbdTrue: number
    jtbdFalse: number
  }>[] = [
    {
      key: 'agent',
      title: t('dashboardAgent'),
      dataKey: 'agent',
    },
    {
      key: 'totalEvaluations',
      title: t('dashboardTotalEvaluations'),
      dataKey: 'totalEvaluations',
    },
    {
      key: 'jtbdTrue',
      title: t('dashboardJtbdTrue'),
      dataKey: 'jtbdTrue',
    },
    {
      key: 'jtbdCR',
      title: t('dashboardJtbdCR'),
      dataKey: 'jtbdTrue',
      render: ({ record }) => {
        const cr = record.jtbdTrue / record.totalEvaluations

        return <Text>{formatNumber(+(cr || 0) * 100)}%</Text>
      },
    },
  ]

  const getContentTotalFiltered = () => {
    const activeFilters = Object.keys(filter)?.filter(
      (key) => !!filter[key] && key !== 'dateAtAfter' && key !== 'dateAtBefore',
    ).length

    if (activeFilters > 0) {
      return (
        <Tag variant="primary" model="light" size="small">
          {activeFilters}
        </Tag>
      )
    }

    return ''
  }

  return (
    <div className={styles.core}>
      <div className={styles.section}>
        <Text variant="title-medium">{t('dashboardMonthlyMetricsTitle')}</Text>

        <Alert title={t('dashboardMonthlyMetricsAlertTitle')} variant="primary">
          <Text>{t('dashboardMonthlyMetricsAlertDescription')}</Text>
        </Alert>

        <div className={styles.filter}>
          <AppliedFilters
            translations={{
              filterAppliedText: t('conversationTableFilterAppliedText'),
            }}
            filtersApplied={filter}
            onRemoveFilter={(key) => setFilter({ ...filter, [key]: '' })}
            keyTranslations={{
              role: t('conversationTableFilterRole'),
            }}
            renderValue={(key: string, value: string) => {
              if (key === 'role') {
                return tEnums(`conversation_role_${value}`)
              }

              return value
            }}
            excludeKeys={['dateAtAfter', 'dateAtBefore']}
          />

          <Button
            variant="secondary"
            onClick={() => setIsOpenDrawerFilter(true)}
          >
            <FilterIcon />
            {t('conversationTableFilterButton')}
            {getContentTotalFiltered()}
          </Button>
        </div>

        <TableColumns
          columns={columns}
          data={monthlyMetrics?.data?.monthlyMetrics || []}
          isLoading={isLoadingMonthlyMetrics}
        />
      </div>

      <div className={styles.section}>
        <Text variant="title-medium">{t('dashboardAgentJTBDStatsTitle')}</Text>

        <Alert
          title={t('dashboardAgentJTBDStatsAlertTitle')}
          variant="primary"
          className={styles.space}
        >
          <Text>{t('dashboardAgentJTBDStatsAlertDescription')}</Text>
        </Alert>

        <MonthlyFilter
          onSelectedMonth={onSelectMonthFilter}
          selectedMonth={getMonthFilter()}
        />

        <div className={styles.filter}>
          <AppliedFilters
            translations={{
              filterAppliedText: t('conversationTableFilterAppliedText'),
            }}
            filtersApplied={filter}
            onRemoveFilter={(key) => setFilter({ ...filter, [key]: '' })}
            keyTranslations={{
              role: t('conversationTableFilterRole'),
            }}
            renderValue={(key: string, value: string) => {
              if (key === 'role') {
                return tEnums(`conversation_role_${value}`)
              }

              return value
            }}
            excludeKeys={['dateAtAfter', 'dateAtBefore']}
          />

          <Button
            variant="secondary"
            onClick={() => setIsOpenDrawerFilter(true)}
          >
            <FilterIcon />
            {t('conversationTableFilterButton')}
            {getContentTotalFiltered()}
          </Button>
        </div>

        <TableColumns
          columns={agentColumns}
          data={monthlyMetrics?.data?.agentJTBDStats || []}
          isLoading={isLoadingMonthlyMetrics}
        />
      </div>

      <DashboardDrawerFilter
        isOpen={isOpenDrawerFilter}
        onHandleOpen={() => setIsOpenDrawerFilter(!isOpenDrawerFilter)}
        filter={filter}
        onHandleFilter={setFilter}
      />
    </div>
  )
}
